import React, { memo, useReducer, useRef } from 'react';
import { Form } from '@redlink/components/organisms';
import { Space, withPopUp } from '@redlink/components/atoms';
import {
  FormSection,
  TextInput,
  useDialog,
} from '@redlink/components/molecules';
import { CategoryItems } from '@redlink/components/muscles';
import { DataEntryModalTemplate } from '@redlink/components/templates';
import { useLocale } from '@redlink/assets/locale';
import {
  useMutation,
  useQuery,
  getPageCategoriesQuery,
  newCategoryQuery,
  editCategoryQuery,
  removeCategoryQuery,
} from '@redlink/services';
import { initialState, reducer, types } from './store';
import { freshCache, freshCacheForRemoveItem } from './cache';

export const CategoryEdit = memo(
  ({
    onDismiss,
    type,
    pageName,
    initialSelectedItem,
    style,
    selectable = false,
  }) => {
    const titleInputRef = useRef();
    const { dictionary } = useLocale();
    const { showConfirmDialog } = useDialog();
    const [{ title, isEditMode, selectedItem }, dispatch] = useReducer(
      reducer,
      initialState({ selectedItem: initialSelectedItem })
    );

    const {
      data: { Categories = [] },
    } = useQuery(getPageCategoriesQuery, {
      variables: { type },
    });

    const [submitCategory, { loading: submitCategoryIsLoading }] = useMutation(
      !isEditMode ? newCategoryQuery : editCategoryQuery,
      {
        done: () =>
          dispatch({
            type: types.SET_MULTI,
            payload: { isEditMode: false, title: '' },
          }),
        update: freshCache({ isEditMode, id: selectedItem?.id, type, pageName }),
      }
    );

    const [removeCategory, { loading: removeCategoryIsBusy }] = useMutation(
      removeCategoryQuery,
      {
        update: freshCacheForRemoveItem({ type, pageName }),
      }
    );

    const onDeletePressed = (item, index) => {
      showConfirmDialog({
        message: dictionary.deleteConfirmMessage,
        onConfirm: () => removeCategory({ id: item.id }),
      });
    };

    const onEditPressed = (item, index) => {
      dispatch({
        type: types.SET_MULTI,
        payload: { isEditMode: true, selectedItem: item, title: item.title },
      });
      titleInputRef.current.focus();
    };

    const onSelectItem = (item, index) => {
      dispatch({
        type: types.SET_MULTI,
        payload: { isEditMode: false, selectedItem: item, title: '' },
      });
    };

    return (
      <Form
        {...{ style }}
        isBusy={submitCategoryIsLoading || removeCategoryIsBusy}
        actions={[
          {
            title: selectable ? dictionary.selectAndBack : dictionary.back,
            variant: 'secondary',
            onPress: () => {
              onDismiss && onDismiss(selectedItem);
            },
          },
        ]}
      >
        <FormSection title={dictionary.categories}>
          <TextInput
            ref={titleInputRef}
            title={dictionary.categoryTitle}
            value={title}
            withActionButton
            actionButtonTitle={dictionary.add}
            onActionButtonPress={() =>
              submitCategory({
                id: selectedItem?.id,
                title,
                type,
              })
            }
            onChangeText={(value) =>
              dispatch({ type: types.SET_TITLE, payload: value })
            }
          />
          <Space size={8} />
          <CategoryItems
            data={Categories}
            onDeletePress={onDeletePressed}
            onEditPress={onEditPressed}
            onSelectItem={onSelectItem}
            {...{ selectedItem }}
          />
        </FormSection>
      </Form>
    );
  }
);

export const CategoryEditPage = withPopUp(
  memo((props) => {
    return (
      <DataEntryModalTemplate>
        <CategoryEdit {...props} />
      </DataEntryModalTemplate>
    );
  })
);
